import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Account } from "@/hooks/useAccounts";
import type { CreateInstallmentRequest } from "@/hooks/useInstallments";
import { ModalShell } from "@/components/ui/ModalShell.tsx";
import { FormField, ModalActions, SelectInput, TextArea, TextInput } from "@/components/ui/primitives.tsx";
import { AmountField } from "@/components/ui/AmountField";
import { amountToInput } from "@/utils/amountInput";
import "../installments-form.css";

interface InstallmentsDebtFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  accounts: Account[];
  initialData?: (CreateInstallmentRequest & { id?: number }) | null;
  onSubmit: (data: CreateInstallmentRequest, id?: number) => void;
}

const toNumber = (value: string, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toOptionalNumber = (value: string): number | null => {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const InstallmentsDebtFormModal: React.FC<InstallmentsDebtFormModalProps> = ({
  isOpen,
  onClose,
  accounts,
  initialData,
  onSubmit,
}) => {
  const { t } = useTranslation(["installments", "common", "budget"]);
  const isEdit = Boolean(initialData?.id);

  const [formState, setFormState] = useState<CreateInstallmentRequest>(() => ({
    name: initialData?.name ?? "",
    account_id: initialData?.account_id ?? (accounts[0]?.id ?? 0),
    description: initialData?.description ?? "",
    initial_amount: initialData?.initial_amount ?? 0,
    amount: initialData?.amount ?? 0,
    interest_rate: initialData?.interest_rate ?? null,
    start_date: initialData?.start_date ?? "",
    num_payments: initialData?.num_payments ?? undefined,
    done_payments: initialData?.done_payments ?? 0,
    active: initialData?.active ?? true,
  }));
  const [amountInput, setAmountInput] = useState(() =>
    amountToInput(initialData?.amount)
  );
  const [initialAmountInput, setInitialAmountInput] = useState(() =>
    initialData?.initial_amount !== undefined && initialData?.initial_amount !== null
      ? String(initialData.initial_amount)
      : ""
  );
  const [interestRateInput, setInterestRateInput] = useState(() =>
    initialData?.interest_rate !== undefined && initialData?.interest_rate !== null
      ? String(initialData.interest_rate)
      : ""
  );

  const sortedAccounts = useMemo(
    () => [...accounts].sort((a, b) => a.name.localeCompare(b.name)),
    [accounts],
  );
  useEffect(() => {
    if (!isOpen) return;
    setFormState({
      name: initialData?.name ?? "",
      account_id: initialData?.account_id ?? (sortedAccounts[0]?.id ?? 0),
      description: initialData?.description ?? "",
      initial_amount: initialData?.initial_amount ?? 0,
      amount: initialData?.amount ?? 0,
      interest_rate: initialData?.interest_rate ?? null,
      start_date: initialData?.start_date ?? "",
      num_payments: initialData?.num_payments ?? undefined,
      done_payments: initialData?.done_payments ?? 0,
      active: initialData?.active ?? true,
    });
    setAmountInput(
      amountToInput(initialData?.amount)
    );
    setInitialAmountInput(
      initialData?.initial_amount !== undefined && initialData?.initial_amount !== null
        ? String(initialData.initial_amount)
        : ""
    );
    setInterestRateInput(
      initialData?.interest_rate !== undefined && initialData?.interest_rate !== null
        ? String(initialData.interest_rate)
        : ""
    );

  }, [initialData, isOpen, sortedAccounts]);

  const parsedInterestRate = toOptionalNumber(interestRateInput);
  const interestRateError =
    interestRateInput.trim().length > 0 &&
    (parsedInterestRate === null || parsedInterestRate < 0);

  const canSubmit = useMemo(() => {
    return (
      formState.name.trim().length > 0 &&
      formState.account_id > 0 &&
      amountInput.trim().length > 0 &&
      formState.num_payments !== undefined &&
      formState.num_payments > 0 &&
      !interestRateError
    );
  }, [formState, amountInput, interestRateError]);

  const handleChange = (
    field: keyof CreateInstallmentRequest,
    value: string | number | boolean | null | undefined,
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    const payload: CreateInstallmentRequest = {
      name: formState.name.trim(),
      account_id: formState.account_id,
      description: formState.description?.trim() || undefined,
      initial_amount: toNumber(initialAmountInput, 0),
      amount: toNumber(amountInput, 0),
      interest_rate: parsedInterestRate,
      start_date: formState.start_date || undefined,
      num_payments: formState.num_payments,
      done_payments: formState.done_payments ?? 0,
      active: formState.active ?? true,
    };

    if (isEdit && initialData?.done_payments === payload.done_payments) {
      delete payload.done_payments;
    }

    onSubmit(payload, initialData?.id);
  };

  const optionalHint = <span className="app-form-required">{t("common.labels.optional")}</span>;
  const subtitle = isEdit ? undefined : t("installments.newInstallmentSubtitle", { defaultValue: "Compra a meses" });

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="installment-modal-title"
      title={isEdit ? t("installments.editInstallment") : t("installments.newInstallment")}
      subtitle={subtitle}
      mobileSheet
      variant="creation"
      maxWidthClassName="!w-[min(100%,640px)] !max-w-[640px]"
      panelClassName="account-modal-panel installment-modal-panel !h-fit"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      footer={
        <ModalActions
          cancelLabel={t("common.actions.cancel")}
          submitLabel={isEdit ? t("installments.saveChanges") : t("installments.createInstallment")}
          onCancel={onClose}
          isSubmitDisabled={!canSubmit}
        />
      }
    >
      <div className="installment-form">
        <p className="app-domain-sidebar-label installment-form-section">{t("budget.principalSection")}</p>
        <div className="installment-form-grid">
          <FormField label={t("common.labels.name")} htmlFor="installment-name" required>
            <TextInput
              id="installment-name"
              value={formState.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder={t("installments.namePlaceholder")}
              required
              autoFocus
            />
          </FormField>

          <FormField label={t("installments.startDate")} htmlFor="installment-start-date" required>
            <TextInput
              id="installment-start-date"
              type="date"
              value={formState.start_date ?? ""}
              onChange={(e) => handleChange("start_date", e.target.value)}
            />
          </FormField>

          <FormField className="installment-form-full" label={t("common.labels.amount")} htmlFor="installment-amount" required>
            <AmountField
              id="installment-amount"
              value={amountInput}
              onChange={(value) => {
                setAmountInput(value);
                handleChange("amount", toNumber(value));
              }}
              centsLabel={t("budget.amountCents")}
              required
            />
          </FormField>

          <FormField label={t("installments.installments")} htmlFor="installment-installments" required>
            <TextInput
              id="installment-installments"
              type="text"
              inputMode="numeric"
              required
              value={formState.num_payments ?? ""}
              onChange={(e) =>
                handleChange("num_payments", e.target.value ? toNumber(e.target.value) : undefined)
              }
            />
          </FormField>

          <FormField
            label={<>{t("installments.annualInterestRate")}{optionalHint}</>}
            htmlFor="installment-interest-rate"
            helper={t("installments.interestRateHelpShort", { defaultValue: "Déjala en 0 si es sin intereses. Puedes completarla después." })}
            error={interestRateError ? t("installments.interestRateInvalid") : undefined}
          >
            <TextInput
              id="installment-interest-rate"
              type="text"
              inputMode="decimal"
              value={interestRateInput}
              onChange={(e) => {
                const nextValue = e.target.value;
                setInterestRateInput(nextValue);
                handleChange("interest_rate", toOptionalNumber(nextValue));
              }}
              placeholder="0"
              aria-invalid={interestRateError}
            />
          </FormField>
        </div>

        {/* Not in the catalog form, but the API still needs them (account is required). */}
        <p className="app-domain-sidebar-label installment-form-section installment-form-section-extra">
          {t("installments.detailsSection", { defaultValue: "Detalles" })}
        </p>
        <div className="installment-form-grid">
          <FormField className="installment-form-full" label={t("common.labels.account")} htmlFor="installment-account" required>
            <SelectInput
              id="installment-account"
              value={formState.account_id}
              onChange={(e) => handleChange("account_id", Number(e.target.value))}
              required
            >
              {sortedAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </SelectInput>
          </FormField>

          <FormField label={<>{t("installments.initialAmount")}{optionalHint}</>} htmlFor="installment-initial-amount">
            <TextInput
              id="installment-initial-amount"
              type="text"
              inputMode="decimal"
              value={initialAmountInput}
              onChange={(e) => {
                const nextValue = e.target.value;
                setInitialAmountInput(nextValue);
                handleChange("initial_amount", toNumber(nextValue));
              }}
              placeholder="0.00"
            />
          </FormField>

          <FormField label={<>{t("installments.donePayments")}{optionalHint}</>} htmlFor="installment-done-payments">
            <TextInput
              id="installment-done-payments"
              type="text"
              inputMode="numeric"
              value={formState.done_payments ?? 0}
              onChange={(e) => handleChange("done_payments", toNumber(e.target.value))}
            />
          </FormField>

          <FormField className="installment-form-full" label={<>{t("common.labels.description")}{optionalHint}</>} htmlFor="installment-description">
            <TextArea
              id="installment-description"
              value={formState.description ?? ""}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder={t("installments.notesHelp")}
              rows={2}
            />
          </FormField>

          <label className="installment-form-full installment-form-check">
            <input
              id="installment-active"
              type="checkbox"
              checked={formState.active ?? true}
              onChange={(e) => handleChange("active", e.target.checked)}
            />
            {t("installments.active")}
          </label>
        </div>
      </div>
    </ModalShell>
  );
};
